import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Check,
  CircleAlert,
  ExternalLink,
  FilePlus2,
  History,
  Plus,
  Trash2,
} from "lucide-react";

import CatalogEditorSection from "./CatalogEditorSection";
import CatalogImageField from "./CatalogImageField";
import CatalogInspector from "./CatalogInspector";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const catalogApi = `${process.env.REACT_APP_BACKEND_URL}/api/catalog`;

const blankRecord = (recordType) => ({
  record_type: recordType,
  name: "",
  display_name: "",
  short_name: "",
  abbreviation: "",
  slug: "",
  description: "",
  internal_notes: "",
  parents: {},
  language: "",
  region: "",
  publisher: "",
  release_date: "",
  original_release_date: "",
  end_date: "",
  set_type: "",
  classification: "",
  official_set_code: "",
  alternate_names: [],
  search_aliases: [],
  printed_card_count: "",
  total_card_count: "",
  secret_card_count: "",
  display_order: 0,
  release_order: "",
  active: true,
  status: "draft",
  images: { gallery_image_ids: [] },
  external_mappings: [],
  extra_fields: {},
});

const imageSlots = [
  ["primary_image_id", "Primary image"],
  ["logo_image_id", "Logo"],
  ["symbol_image_id", "Set symbol"],
  ["icon_image_id", "Icon"],
  ["banner_image_id", "Banner"],
];

const relationshipFields = [
  ["category_id", "Catalog category", "catalog_category"],
  ["game_id", "Game", "game"],
  ["variant_id", "Game variant", "game_variant"],
  ["era_id", "Era / series / block", "era"],
  ["set_id", "Parent set", "set"],
];

const defaultSections = {
  counts: false,
  general: true,
  images: true,
  internal: false,
  mappings: false,
  publishing: true,
  relationships: true,
  release: true,
  search: false,
};

const csvToList = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeRecordForDirtyState = (record) => {
  const normalized = { ...record };
  normalized.alternate_names = csvToList(
    record.alternate_names_text || record.alternate_names?.join(",") || "",
  );
  normalized.search_aliases = csvToList(
    record.search_aliases_text || record.search_aliases?.join(",") || "",
  );
  delete normalized.alternate_names_text;
  delete normalized.search_aliases_text;
  return JSON.stringify(normalized);
};

const typeLabel = (type) =>
  (type || "record")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

function Field({ children, label, testId, wide = false }) {
  return (
    <label
      className={`catalog-editor-field ${wide ? "is-wide" : ""}`}
      data-testid={testId}
    >
      <span>{label}</span>
      {children}
    </label>
  );
}

export default function CatalogRecordEditor({
  onConsumeQuickCreated,
  onNavigateRecord,
  onNavigateType,
  onOpenChange,
  onQuickCreate,
  onSave,
  onViewHistory,
  open,
  parentOptions,
  quickCreated,
  record,
  recordType,
}) {
  const [form, setForm] = useState(blankRecord(recordType));
  const [baseline, setBaseline] = useState("");
  const [manualSlug, setManualSlug] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [assetMeta, setAssetMeta] = useState({});
  const [validationError, setValidationError] = useState("");
  const [mobileTab, setMobileTab] = useState("editor");
  const [sections, setSections] = useState(() => {
    try {
      return {
        ...defaultSections,
        ...JSON.parse(localStorage.getItem("catalog-editor-sections") || "{}"),
      };
    } catch {
      return defaultSections;
    }
  });

  useEffect(() => {
    localStorage.setItem("catalog-editor-sections", JSON.stringify(sections));
  }, [sections]);

  useEffect(() => {
    const nextForm = record
      ? { ...blankRecord(record.record_type), ...record }
      : blankRecord(recordType);
    setForm(nextForm);
    setBaseline(normalizeRecordForDirtyState(nextForm));
    setManualSlug(Boolean(record?.slug));
    setAssetMeta({});
    setValidationError("");
    setMobileTab("editor");
  }, [open, record, recordType]);

  useEffect(() => {
    if (!quickCreated?.field || !quickCreated?.record) {
      return;
    }
    setForm((current) => ({
      ...current,
      parents: {
        ...current.parents,
        [quickCreated.field]: quickCreated.record.record_id,
      },
    }));
    onConsumeQuickCreated();
  }, [onConsumeQuickCreated, quickCreated]);

  const isDirty = normalizeRecordForDirtyState(form) !== baseline;
  const completionPercent = Math.round(
    ([
      Boolean(form.name),
      Boolean(form.release_date),
      Boolean(form.images?.primary_image_id),
      Boolean(form.status === "published"),
    ].filter(Boolean).length /
      4) *
      100,
  );
  const selectedParents = useMemo(
    () =>
      relationshipFields
        .map(([field]) => form.parents?.[field])
        .filter(Boolean),
    [form.parents],
  );

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateName = (value) => {
    if (value.trim()) {
      setValidationError("");
    }
    setForm((current) => ({
      ...current,
      name: value,
      display_name: current.display_name || value,
      slug: manualSlug
        ? current.slug
        : value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, ""),
    }));
  };

  const updateParent = (field, value) => {
    setForm((current) => ({
      ...current,
      parents: { ...current.parents, [field]: value || null },
    }));
  };

  const readImageMetadata = (file) =>
    new Promise((resolve) => {
      const image = new Image();
      const url = URL.createObjectURL(file);
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve({
          fileSize: file.size,
          height: image.naturalHeight,
          width: image.naturalWidth,
        });
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ fileSize: file.size });
      };
      image.src = url;
    });

  const uploadImage = async (slot, file) => {
    if (!file) {
      return;
    }
    setUploading(slot);
    try {
      const payload = new FormData();
      payload.append("file", file);
      payload.append(
        "alt_text",
        form.display_name || form.name || typeLabel(recordType),
      );
      payload.append("image_type", slot.replace("_image_id", ""));
      const response = await axios.post(`${catalogApi}/assets`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      const metadata = await readImageMetadata(file);
      setAssetMeta((current) => ({
        ...current,
        [response.data.asset_id]: metadata,
      }));
      setForm((current) => ({
        ...current,
        images: { ...current.images, [slot]: response.data.asset_id },
      }));
    } finally {
      setUploading("");
    }
  };

  const removeImage = (slot) => {
    setForm((current) => ({
      ...current,
      images: { ...current.images, [slot]: null },
    }));
  };

  const uploadGallery = async (files) => {
    const selectedFiles = Array.from(files || []);
    if (!selectedFiles.length) {
      return;
    }
    setUploading("gallery");
    try {
      const uploads = await Promise.all(
        selectedFiles.map(async (file) => {
          const payload = new FormData();
          payload.append("file", file);
          payload.append(
            "alt_text",
            form.display_name || form.name || "Catalog asset",
          );
          payload.append("image_type", "gallery");
          const response = await axios.post(`${catalogApi}/assets`, payload, {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          });
          return {
            asset: response.data,
            metadata: await readImageMetadata(file),
          };
        }),
      );
      setAssetMeta((current) => ({
        ...current,
        ...Object.fromEntries(
          uploads.map(({ asset, metadata }) => [asset.asset_id, metadata]),
        ),
      }));
      setForm((current) => ({
        ...current,
        images: {
          ...current.images,
          gallery_image_ids: [
            ...(current.images.gallery_image_ids || []),
            ...uploads.map(({ asset }) => asset.asset_id),
          ],
        },
      }));
    } finally {
      setUploading("");
    }
  };

  const removeGalleryImage = (assetId) => {
    setForm((current) => ({
      ...current,
      images: {
        ...current.images,
        gallery_image_ids: current.images.gallery_image_ids.filter(
          (id) => id !== assetId,
        ),
      },
    }));
  };

  const addMapping = () => {
    setForm((current) => ({
      ...current,
      external_mappings: [
        ...current.external_mappings,
        {
          active: true,
          category_id: "",
          category_name: "",
          external_url: "",
          group_or_set_id: "",
          notes: "",
          platform_name: "",
        },
      ],
    }));
  };

  const updateMapping = (index, field, value) => {
    setForm((current) => ({
      ...current,
      external_mappings: current.external_mappings.map(
        (mapping, mappingIndex) =>
          mappingIndex === index ? { ...mapping, [field]: value } : mapping,
      ),
    }));
  };

  const removeMapping = (index) => {
    setForm((current) => ({
      ...current,
      external_mappings: current.external_mappings.filter(
        (_, mappingIndex) => mappingIndex !== index,
      ),
    }));
  };

  const preparePayload = (status) => ({
    ...form,
    alternate_names: csvToList(
      form.alternate_names_text || form.alternate_names.join(","),
    ),
    external_mappings: form.external_mappings
      .filter((mapping) => mapping.platform_name.trim())
      .map((mapping) => ({
        ...mapping,
        external_url: mapping.external_url || null,
      })),
    printed_card_count:
      form.printed_card_count === "" ? null : Number(form.printed_card_count),
    release_order:
      form.release_order === "" ? null : Number(form.release_order),
    search_aliases: csvToList(
      form.search_aliases_text || form.search_aliases.join(","),
    ),
    secret_card_count:
      form.secret_card_count === "" ? null : Number(form.secret_card_count),
    status,
    total_card_count:
      form.total_card_count === "" ? null : Number(form.total_card_count),
  });

  const persist = async (status, continueEditing = false) => {
    if (!form.name.trim()) {
      setValidationError(
        "Official name is required before this record can be saved.",
      );
      setSections((current) => ({ ...current, general: true }));
      return;
    }
    setValidationError("");
    setSaving(true);
    try {
      const saved = await onSave(preparePayload(status), continueEditing);
      if (continueEditing) {
        const nextForm = {
          ...blankRecord(recordType),
          active: form.active,
          language: form.language,
          parents: form.parents,
          publisher: form.publisher,
          region: form.region,
        };
        setForm(nextForm);
        setBaseline(normalizeRecordForDirtyState(nextForm));
        setManualSlug(false);
      } else if (saved) {
        const nextForm = { ...blankRecord(saved.record_type), ...saved };
        setForm(nextForm);
        setBaseline(normalizeRecordForDirtyState(nextForm));
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setSections((current) => ({ ...current, [section]: !current[section] }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="catalog-editor-dialog"
        onKeyDown={(event) => {
          if (
            (event.ctrlKey || event.metaKey) &&
            event.key.toLowerCase() === "s"
          ) {
            event.preventDefault();
            persist("draft");
          }
        }}
        data-testid="catalog-record-form-dialog"
      >
        <DialogHeader className="catalog-editor-header">
          <div
            className="catalog-editor-breadcrumbs"
            data-testid="catalog-editor-breadcrumbs"
          >
            <button
              onClick={() => onNavigateType(recordType)}
              type="button"
              data-testid="catalog-breadcrumb-catalog"
            >
              Catalog
            </button>
            <span>›</span>
            <button
              onClick={() => onNavigateType(recordType)}
              type="button"
              data-testid="catalog-breadcrumb-type"
            >
              {typeLabel(recordType)}
            </button>
            {record && (
              <>
                <span>›</span>
                <span>{record.display_name || record.name}</span>
              </>
            )}
          </div>
          <DialogTitle>
            {record ? "Edit catalog record" : `Create ${typeLabel(recordType)}`}
          </DialogTitle>
          <DialogDescription>
            Build this manual reference record with a saved draft or publish
            when it is ready.
          </DialogDescription>
          <div
            className="catalog-completion"
            data-testid="catalog-completion-indicator"
          >
            <span>{completionPercent}% complete</span>
            <div>
              <i style={{ width: `${completionPercent}%` }} />
            </div>
          </div>
        </DialogHeader>

        <div
          className="catalog-mobile-editor-tabs"
          data-testid="catalog-mobile-editor-tabs"
        >
          <button
            className={mobileTab === "editor" ? "is-active" : ""}
            onClick={() => setMobileTab("editor")}
            type="button"
            data-testid="catalog-mobile-editor-tab"
          >
            Editor
          </button>
          <button
            className={mobileTab === "preview" ? "is-active" : ""}
            onClick={() => setMobileTab("preview")}
            type="button"
            data-testid="catalog-mobile-preview-tab"
          >
            Preview
          </button>
        </div>

        <div className="catalog-editor-workspace">
          <form
            className={`catalog-editor-form ${mobileTab === "preview" ? "is-mobile-hidden" : ""}`}
            onSubmit={(event) => {
              event.preventDefault();
              persist("draft");
            }}
          >
            <CatalogEditorSection
              id="general"
              isOpen={sections.general}
              onToggle={() => toggleSection("general")}
              summary="Names, code, slug, and description"
              title="General information"
            >
              <div className="catalog-editor-grid">
                <Field label="Official name *" testId="catalog-name-field">
                  <input
                    value={form.name}
                    onChange={(event) => updateName(event.target.value)}
                    data-testid="catalog-name-input"
                  />
                </Field>
                <Field label="Display name" testId="catalog-display-name-field">
                  <input
                    value={form.display_name || ""}
                    onChange={(event) =>
                      update("display_name", event.target.value)
                    }
                    data-testid="catalog-display-name-input"
                  />
                </Field>
                <Field label="Abbreviation" testId="catalog-abbreviation-field">
                  <input
                    value={form.abbreviation || ""}
                    onChange={(event) =>
                      update("abbreviation", event.target.value)
                    }
                    data-testid="catalog-abbreviation-input"
                  />
                </Field>
                <Field label="Official code" testId="catalog-code-field">
                  <input
                    value={form.official_set_code || ""}
                    onChange={(event) =>
                      update("official_set_code", event.target.value)
                    }
                    data-testid="catalog-code-input"
                  />
                </Field>
                <Field label="Slug" testId="catalog-slug-field">
                  <input
                    value={form.slug || ""}
                    onChange={(event) => {
                      setManualSlug(true);
                      update("slug", event.target.value);
                    }}
                    data-testid="catalog-slug-input"
                  />
                </Field>
                <Field label="Short name" testId="catalog-short-name-field">
                  <input
                    value={form.short_name || ""}
                    onChange={(event) =>
                      update("short_name", event.target.value)
                    }
                    data-testid="catalog-short-name-input"
                  />
                </Field>
                <Field
                  label="Description"
                  testId="catalog-description-field"
                  wide
                >
                  <textarea
                    value={form.description || ""}
                    onChange={(event) =>
                      update("description", event.target.value)
                    }
                    data-testid="catalog-description-input"
                  />
                </Field>
              </div>
            </CatalogEditorSection>

            <CatalogEditorSection
              id="relationships"
              isOpen={sections.relationships}
              onToggle={() => toggleSection("relationships")}
              summary="Place this record in the catalog hierarchy"
              title="Relationships"
            >
              <div className="catalog-editor-grid">
                {relationshipFields.map(([field, label, childType]) => (
                  <Field
                    key={field}
                    label={label}
                    testId={`catalog-parent-${field}-field`}
                  >
                    <span className="catalog-parent-control">
                      <select
                        value={form.parents?.[field] || ""}
                        onChange={(event) =>
                          updateParent(field, event.target.value)
                        }
                        data-testid={`catalog-parent-${field}`}
                      >
                        <option value="">None selected</option>
                        {(parentOptions[field] || []).map((option) => (
                          <option
                            key={option.record_id}
                            value={option.record_id}
                          >
                            {option.display_name || option.name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() =>
                          onQuickCreate({ field, type: childType })
                        }
                        title={`Create ${typeLabel(childType)}`}
                        type="button"
                        data-testid={`catalog-quick-create-${field}`}
                      >
                        <Plus size={15} />
                      </button>
                    </span>
                  </Field>
                ))}
                <Field label="Language" testId="catalog-language-field">
                  <input
                    value={form.language || ""}
                    onChange={(event) => update("language", event.target.value)}
                    data-testid="catalog-language-input"
                  />
                </Field>
                <Field label="Region" testId="catalog-region-field">
                  <input
                    value={form.region || ""}
                    onChange={(event) => update("region", event.target.value)}
                    data-testid="catalog-region-input"
                  />
                </Field>
                <Field label="Publisher" testId="catalog-publisher-field">
                  <input
                    value={form.publisher || ""}
                    onChange={(event) =>
                      update("publisher", event.target.value)
                    }
                    data-testid="catalog-publisher-input"
                  />
                </Field>
              </div>
              <div
                className="catalog-relationship-cards"
                data-testid="catalog-relationship-cards"
              >
                {selectedParents.length ? (
                  relationshipFields.map(([field]) => {
                    const selected = (parentOptions[field] || []).find(
                      (option) => option.record_id === form.parents?.[field],
                    );
                    return selected ? (
                      <button
                        key={field}
                        onClick={() => onNavigateRecord(selected)}
                        type="button"
                        data-testid={`catalog-relationship-${field}`}
                      >
                        {selected.display_name || selected.name}
                      </button>
                    ) : null;
                  })
                ) : (
                  <p>
                    No hierarchy links yet. Add a parent when this record
                    belongs under another catalog record.
                  </p>
                )}
              </div>
            </CatalogEditorSection>

            <CatalogEditorSection
              id="release"
              isOpen={sections.release}
              onToggle={() => toggleSection("release")}
              summary="Timing and classification"
              title="Release information"
            >
              <div className="catalog-editor-grid">
                <Field label="Release date" testId="catalog-release-date-field">
                  <input
                    type="date"
                    value={form.release_date || ""}
                    onChange={(event) =>
                      update("release_date", event.target.value || null)
                    }
                    data-testid="catalog-release-date-input"
                  />
                </Field>
                <Field
                  label="Original release date"
                  testId="catalog-original-release-date-field"
                >
                  <input
                    type="date"
                    value={form.original_release_date || ""}
                    onChange={(event) =>
                      update(
                        "original_release_date",
                        event.target.value || null,
                      )
                    }
                    data-testid="catalog-original-release-date-input"
                  />
                </Field>
                <Field label="End date" testId="catalog-end-date-field">
                  <input
                    type="date"
                    value={form.end_date || ""}
                    onChange={(event) =>
                      update("end_date", event.target.value || null)
                    }
                    data-testid="catalog-end-date-input"
                  />
                </Field>
                <Field label="Set type" testId="catalog-set-type-field">
                  <input
                    value={form.set_type || ""}
                    onChange={(event) => update("set_type", event.target.value)}
                    data-testid="catalog-set-type-input"
                  />
                </Field>
                <Field
                  label="Classification"
                  testId="catalog-classification-field"
                >
                  <input
                    value={form.classification || ""}
                    onChange={(event) =>
                      update("classification", event.target.value)
                    }
                    data-testid="catalog-classification-input"
                  />
                </Field>
                <Field
                  label="Release order"
                  testId="catalog-release-order-field"
                >
                  <input
                    type="number"
                    value={form.release_order ?? ""}
                    onChange={(event) =>
                      update("release_order", event.target.value)
                    }
                    data-testid="catalog-release-order-input"
                  />
                </Field>
              </div>
            </CatalogEditorSection>

            <CatalogEditorSection
              id="counts"
              isOpen={sections.counts}
              onToggle={() => toggleSection("counts")}
              summary="Printed, total, and secret counts"
              title="Card counts"
            >
              <div className="catalog-editor-grid">
                <Field
                  label="Printed count"
                  testId="catalog-printed-count-field"
                >
                  <input
                    type="number"
                    value={form.printed_card_count ?? ""}
                    onChange={(event) =>
                      update("printed_card_count", event.target.value)
                    }
                    data-testid="catalog-printed-count-input"
                  />
                </Field>
                <Field label="Total count" testId="catalog-total-count-field">
                  <input
                    type="number"
                    value={form.total_card_count ?? ""}
                    onChange={(event) =>
                      update("total_card_count", event.target.value)
                    }
                    data-testid="catalog-total-count-input"
                  />
                </Field>
                <Field label="Secret count" testId="catalog-secret-count-field">
                  <input
                    type="number"
                    value={form.secret_card_count ?? ""}
                    onChange={(event) =>
                      update("secret_card_count", event.target.value)
                    }
                    data-testid="catalog-secret-count-input"
                  />
                </Field>
              </div>
            </CatalogEditorSection>

            <CatalogEditorSection
              id="images"
              isOpen={sections.images}
              onToggle={() => toggleSection("images")}
              summary="Artwork, icons, logos, and symbols"
              title="Images & branding"
            >
              <div className="catalog-image-grid">
                {imageSlots.map(([slot, label]) => (
                  <CatalogImageField
                    assetMeta={assetMeta}
                    imageId={form.images?.[slot]}
                    imageType={slot}
                    key={slot}
                    label={label}
                    onRemove={() => removeImage(slot)}
                    onUpload={(file) => uploadImage(slot, file)}
                    uploading={uploading === slot}
                  />
                ))}
              </div>
              <label
                className="catalog-gallery-dropzone"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  uploadGallery(event.dataTransfer.files);
                }}
                data-testid="catalog-gallery-dropzone"
              >
                <FilePlus2 size={18} />
                <span>Add additional images</span>
                <small>Drop files here or browse. Stored after upload.</small>
                <input
                  accept=".png,.jpg,.jpeg,.webp,.svg"
                  multiple
                  onChange={(event) => uploadGallery(event.target.files)}
                  type="file"
                  data-testid="catalog-upload-gallery-images"
                />
              </label>
              <div
                className="catalog-gallery-preview"
                data-testid="catalog-gallery-preview"
              >
                {(form.images?.gallery_image_ids || []).map((assetId) => (
                  <figure key={assetId}>
                    <img
                      src={`${catalogApi}/assets/${assetId}`}
                      alt="Gallery asset"
                    />
                    <button
                      onClick={() => removeGalleryImage(assetId)}
                      type="button"
                      data-testid={`catalog-remove-gallery-${assetId}`}
                    >
                      <Trash2 size={13} /> Remove
                    </button>
                  </figure>
                ))}
              </div>
            </CatalogEditorSection>

            <CatalogEditorSection
              id="search"
              isOpen={sections.search}
              onToggle={() => toggleSection("search")}
              summary="Aliases and alternate names"
              title="Search & discovery"
            >
              <div className="catalog-editor-grid">
                <Field
                  label="Alternate names"
                  testId="catalog-alternate-names-field"
                  wide
                >
                  <input
                    value={
                      form.alternate_names_text ??
                      form.alternate_names?.join(", ") ??
                      ""
                    }
                    onChange={(event) =>
                      update("alternate_names_text", event.target.value)
                    }
                    placeholder="Comma-separated names"
                    data-testid="catalog-alternate-names-input"
                  />
                </Field>
                <Field
                  label="Search aliases"
                  testId="catalog-search-aliases-field"
                  wide
                >
                  <input
                    value={
                      form.search_aliases_text ??
                      form.search_aliases?.join(", ") ??
                      ""
                    }
                    onChange={(event) =>
                      update("search_aliases_text", event.target.value)
                    }
                    placeholder="Codes, abbreviations, common names"
                    data-testid="catalog-search-aliases-input"
                  />
                </Field>
                <div
                  className="catalog-keyword-note"
                  data-testid="catalog-search-keywords"
                >
                  <strong>Search keywords</strong>
                  <span>
                    {[
                      form.name,
                      form.display_name,
                      form.abbreviation,
                      form.official_set_code,
                    ]
                      .filter(Boolean)
                      .join(" · ") ||
                      "Add a name, abbreviation, or code to build discovery terms."}
                  </span>
                </div>
              </div>
            </CatalogEditorSection>

            <CatalogEditorSection
              id="mappings"
              isOpen={sections.mappings}
              onToggle={() => toggleSection("mappings")}
              summary="TCGPlayer, eBay, Cardmarket, and future platforms"
              title="External platform mappings"
            >
              <div className="catalog-mapping-list">
                {form.external_mappings.map((mapping, index) => (
                  <div
                    className="catalog-mapping-row"
                    key={`${mapping.platform_name}-${index}`}
                  >
                    <input
                      value={mapping.platform_name}
                      onChange={(event) =>
                        updateMapping(
                          index,
                          "platform_name",
                          event.target.value,
                        )
                      }
                      placeholder="Platform (e.g. TCGPlayer)"
                      data-testid={`catalog-mapping-platform-${index}`}
                    />
                    <input
                      value={mapping.category_name || ""}
                      onChange={(event) =>
                        updateMapping(
                          index,
                          "category_name",
                          event.target.value,
                        )
                      }
                      placeholder="Category"
                      data-testid={`catalog-mapping-category-${index}`}
                    />
                    <input
                      value={mapping.category_id || ""}
                      onChange={(event) =>
                        updateMapping(index, "category_id", event.target.value)
                      }
                      placeholder="Category ID"
                      data-testid={`catalog-mapping-category-id-${index}`}
                    />
                    <input
                      value={mapping.group_or_set_id || ""}
                      onChange={(event) =>
                        updateMapping(
                          index,
                          "group_or_set_id",
                          event.target.value,
                        )
                      }
                      placeholder="Group / set ID"
                      data-testid={`catalog-mapping-set-id-${index}`}
                    />
                    <input
                      value={mapping.external_url || ""}
                      onChange={(event) =>
                        updateMapping(index, "external_url", event.target.value)
                      }
                      placeholder="External URL"
                      data-testid={`catalog-mapping-url-${index}`}
                    />
                    <button
                      onClick={() => removeMapping(index)}
                      type="button"
                      data-testid={`catalog-remove-mapping-${index}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                className="catalog-secondary-action"
                onClick={addMapping}
                type="button"
                data-testid="catalog-add-mapping-button"
              >
                <Plus size={15} /> Add mapping
              </button>
            </CatalogEditorSection>

            <CatalogEditorSection
              id="publishing"
              isOpen={sections.publishing}
              onToggle={() => toggleSection("publishing")}
              summary="Publication state and order"
              title="Publishing"
            >
              <div className="catalog-editor-grid">
                <Field label="Display order" testId="catalog-order-field">
                  <input
                    type="number"
                    value={form.display_order}
                    onChange={(event) =>
                      update("display_order", Number(event.target.value))
                    }
                    data-testid="catalog-order-input"
                  />
                </Field>
                <label
                  className="catalog-active-toggle"
                  data-testid="catalog-active-toggle-field"
                >
                  <input
                    checked={form.active}
                    onChange={(event) => update("active", event.target.checked)}
                    type="checkbox"
                    data-testid="catalog-active-toggle"
                  />
                  <span>
                    <strong>Active when published</strong>
                    <small>
                      Keep unavailable records out of operational selection.
                    </small>
                  </span>
                </label>
              </div>
            </CatalogEditorSection>

            <CatalogEditorSection
              id="internal"
              isOpen={sections.internal}
              onToggle={() => toggleSection("internal")}
              summary="Notes and immutable change context"
              title="Internal"
            >
              <div className="catalog-editor-grid">
                <Field label="Internal notes" testId="catalog-notes-field" wide>
                  <textarea
                    value={form.internal_notes || ""}
                    onChange={(event) =>
                      update("internal_notes", event.target.value)
                    }
                    data-testid="catalog-notes-input"
                  />
                </Field>
              </div>
              {record ? (
                <div
                  className="catalog-audit-summary"
                  data-testid="catalog-audit-summary"
                >
                  <span>
                    Created {new Date(record.created_at).toLocaleString()} by{" "}
                    {record.created_by_name || "Unknown"}
                  </span>
                  <span>
                    Updated {new Date(record.updated_at).toLocaleString()} by{" "}
                    {record.updated_by_name || "Unknown"}
                  </span>
                  <button
                    onClick={() => onViewHistory(record)}
                    type="button"
                    data-testid="catalog-view-audit-history-button"
                  >
                    <History size={14} /> View audit history
                  </button>
                </div>
              ) : (
                <p className="catalog-internal-empty">
                  Creation and audit details appear after the first save.
                </p>
              )}
            </CatalogEditorSection>
          </form>

          <div
            className={
              mobileTab === "editor"
                ? "catalog-desktop-preview"
                : "catalog-desktop-preview is-mobile-visible"
            }
          >
            <CatalogInspector
              form={form}
              parentOptions={parentOptions}
              record={record}
              status={form.status || "draft"}
            />
          </div>
        </div>

        <div
          className="catalog-sticky-actions"
          data-testid="catalog-sticky-actions"
        >
          <div
            className="catalog-save-state"
            data-testid="catalog-unsaved-indicator"
          >
            {validationError ? (
              <CircleAlert size={15} />
            ) : isDirty ? (
              <span className="catalog-unsaved-dot" />
            ) : (
              <Check size={15} />
            )}
            <span>
              {validationError ||
                (isDirty ? "Unsaved changes" : "All changes saved")}
            </span>
          </div>
          <div>
            <button
              onClick={() => onOpenChange(false)}
              type="button"
              data-testid="catalog-cancel-button"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              onClick={() => persist("draft", true)}
              type="button"
              data-testid="catalog-save-another-button"
            >
              Save & continue
            </button>
            <button
              disabled={saving}
              onClick={() => persist("draft")}
              type="button"
              data-testid="catalog-save-draft-button"
            >
              Save draft
            </button>
            <button
              className="catalog-publish-action"
              disabled={saving}
              onClick={() => persist("published")}
              type="button"
              data-testid="catalog-publish-button"
            >
              Publish <ExternalLink size={14} />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
